import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, Paper } from "@mui/material";
import HealthIcon from "@mui/icons-material/HealthAndSafety";
import { getDashboardAll } from "../../api/dashboard";
import HealthWidget from "./HealthWidget";
import MessageStatsWidget from "./MessageStatsWidget";
import BlockStatsWidget from "./BlockStatsWidget";
import WarmUpWidget from "./WarmUpWidget";
import DailyLimitsWidget from "./DailyLimitsWidget";
import AgentPerformanceWidget from "./AgentPerformanceWidget";
import ContactHealthWidget from "./ContactHealthWidget";
import AlertsWidget from "./AlertsWidget";
import TipsWidget from "./TipsWidget";

export default function ClientMonitoringDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getDashboardAll()
      .then(setData)
      .catch((err) => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper className="glass-card" sx={{ p: 3, textAlign: "center" }}>
        <Typography color="#ef4444" fontWeight={600}>Unable to load monitoring data</Typography>
        <Typography variant="body2" color="#64748b" mt={0.5}>{error}</Typography>
      </Paper>
    );
  }

  if (!data) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "27px" }}>
      <Paper className="glass-card" sx={{
        p: 3,
        bgcolor: "linear-gradient(135deg, rgba(7,94,84,0.08) 0%, rgba(10,138,122,0.08) 100%)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <HealthIcon sx={{ color: "#075e54", fontSize: 24 }} />
          <Typography fontWeight={800} fontSize={20} color="#1e293b">
            Account Monitoring
          </Typography>
        </Box>
        <Typography variant="body2" color="#64748b">
          Real-time health, message stats, and ban prevention insights
        </Typography>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "27px" }}>
        <HealthWidget data={data.health} />
        <BlockStatsWidget data={data.blocks} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "27px" }}>
        <MessageStatsWidget data={data.messages} />
        <WarmUpWidget data={data.warmup} />
        <DailyLimitsWidget data={data.limits} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "27px" }}>
        <AgentPerformanceWidget data={data.agents} />
        <ContactHealthWidget data={data.contacts} />
      </Box>

      <AlertsWidget alerts={data.alerts} />
      <TipsWidget tips={data.tips} />
    </Box>
  );
}
